import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import { siteUsersEnable, siteUsersList } from '@/shared/api/generated/site-users/site-users';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { toast } from '@/shared/ui/Toast';

import { TypeFields } from '../SchemaPage/TypeFields';
import { EmailsList } from './EmailsList';
import type { UsersTab } from './tabs';
import { UsersApi } from './UsersApi';
import { OAuthSettings } from './OAuthSettings';
import { UsersSettings } from './UsersSettings';
import { VisitorsList } from './VisitorsList';
import { VISITORS } from './words';

/**
 * «Пользователи» (шаг 7.1): посетители, которые регистрируются и входят на
 * сайте. Внутри — коллекция с назначением users: её поля — поля профиля.
 * Пока коллекции нет, вход на сайте выключен — раздел предлагает включить.
 */
export function UsersPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/users' });
  const { tab = 'visitors' } = useSearch({ from: '/authed/sites/$siteId/$env/users' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useSiteScope();
  const shaper = scope?.can('site.shape') ?? false;
  const [enabling, setEnabling] = useState(false);

  // Коллекция пользователей — из первой страницы списка: там же и сколько их.
  const summary = useQuery({
    queryKey: ['site-users', siteId, env, 'summary'],
    queryFn: async () => {
      const response = await siteUsersList(siteId, kinds[env], { per_page: 1 });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список посетителей');
      }

      return response.data;
    },
  });

  const poolId = summary.data?.pool?.id;

  const details = useQuery({
    queryKey: ['content-type', poolId],
    enabled: poolId !== undefined,
    queryFn: async () => {
      const response = await contentTypesGet(poolId ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос коллекции');
      }

      return response.data;
    },
  });

  const setTab = (next: string) => {
    void navigate({
      to: '/sites/$siteId/$env/users',
      params: { siteId, env },
      search: next === 'visitors' ? {} : { tab: next as UsersTab },
      replace: true,
    });
  };

  const enable = () => {
    setEnabling(true);

    siteUsersEnable(siteId, kinds[env])
      .then(() => {
        toast.success('Вход на сайте включён');
        void queryClient.invalidateQueries({ queryKey: ['site-users', siteId] });
        void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось включить');
      })
      .finally(() => {
        setEnabling(false);
      });
  };

  if (summary.isPending) {
    return null;
  }

  const pool = details.data?.type ?? summary.data?.pool;

  if (pool === undefined) {
    return (
      <div className={catalog['page']}>
        <CatalogHead
          title="Пользователи"
          subtitle="Посетители, которые регистрируются и входят на сайте: покупатели, ученики, участники."
        />
        <EmptyState
          icon={<Users />}
          title="Вход на сайте не включён"
          description="Включите — и сайт сможет регистрировать посетителей, входить по почте и паролю, восстанавливать пароль. Поля профиля настраиваются как у любой коллекции. На Development и Production посетители свои."
          action={
            shaper ? (
              <Button loading={enabling} onClick={enable}>
                Включить вход на сайте
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Пользователи"
        subtitle={`${pluralize(summary.data?.total ?? 0, VISITORS)} · регистрация ${
          pool.auth?.registration === 'closed' ? 'закрыта' : 'открыта'
        } · ${pool.auth?.confirm_email === false ? 'без подтверждения почты' : 'с подтверждением почты'}`}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="visitors" active={tab === 'visitors'}>
            Посетители
          </TabsTrigger>
          <TabsTrigger value="fields" active={tab === 'fields'}>
            Поля профиля
          </TabsTrigger>
          <TabsTrigger value="emails" active={tab === 'emails'}>
            Письма
          </TabsTrigger>
          <TabsTrigger value="api" active={tab === 'api'}>
            API
          </TabsTrigger>
          {shaper && (
            <TabsTrigger value="settings" active={tab === 'settings'}>
              Настройки
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="visitors">
          <VisitorsList
            key={env}
            siteId={siteId}
            env={env}
            poolId={pool.id}
            unread={pool.unread_count}
            fields={details.data?.fields ?? []}
            canWrite={scope?.can('content.write') ?? false}
          />
        </TabsContent>

        <TabsContent value="fields">
          {details.data !== undefined && <TypeFields type={details.data.type} readOnly={!shaper} />}
        </TabsContent>

        <TabsContent value="emails">
          <EmailsList siteId={siteId} env={env} />
        </TabsContent>

        <TabsContent value="api">
          <UsersApi siteId={siteId} env={env} />
        </TabsContent>

        {shaper && details.data !== undefined && (
          <TabsContent value="settings">
            <UsersSettings
              key={details.data.type.etag}
              type={details.data.type}
              onSaved={() => {
                void details.refetch();
                void summary.refetch();
              }}
            />
            <OAuthSettings siteId={siteId} env={env} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
